<template>
  <div
    v-if="task"
    class="modal-overlay"
    @click.self="store.dismiss"
  >
    <div class="modal-sheet">
      <div class="modal-handle" />
      <h2 style="font-family: 'Manrope', sans-serif; font-weight: 800; font-size: 1.25rem; margin: 0 0 0.5rem;">
        Add this task?
      </h2>

      <form @submit.prevent="onSave">
        <label class="field-label">What</label>
        <input
          v-model="form.title"
          class="field-input"
          required
        >

        <div style="margin-top: 0.875rem;">
          <label class="field-label">Category</label>
          <div class="chip-row">
            <button
              v-for="cat in CATEGORIES"
              :key="cat"
              type="button"
              :class="['chip', `chip-${cat}`, { selected: form.category === cat }]"
              @click="form.category = cat"
            >
              {{ cat }}
            </button>
          </div>
        </div>

        <div style="margin-top: 0.875rem;">
          <label class="field-label">Priority</label>
          <div class="priority-row">
            <button
              v-for="p in PRIORITIES"
              :key="p"
              type="button"
              :class="['priority-opt', { selected: form.priority === p }]"
              @click="form.priority = p"
            >
              {{ p }}
            </button>
          </div>
        </div>

        <div style="margin-top: 0.875rem;">
          <ThemedDateTimeField
            v-model="form.due_at"
            label="When (defaults to end of day)"
          />
        </div>

        <div style="margin-top: 0.875rem;">
          <label
            class="field-label"
            for="ai-confirm-repeat"
          >Repeat (optional iCal RRULE)</label>
          <input
            id="ai-confirm-repeat"
            v-model="form.repeat_rule"
            class="field-input"
            placeholder="e.g. FREQ=DAILY"
          >
        </div>

        <p
          v-if="error"
          class="text-body-sm"
          style="color: var(--color-error); margin-top: 0.5rem;"
        >
          {{ error }}
        </p>

        <div class="modal-sheet-footer">
          <button
            type="button"
            class="btn-tertiary"
            @click="store.dismiss"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="saving || !form.title"
          >
            {{ saving ? "Saving…" : "Save task" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue"
import { useAiTaskConfirmStore, type SuggestedTask } from "@/stores/aiTaskConfirm"
import { apiClient } from "@/api/client"
import ThemedDateTimeField from "@/components/ThemedDateTimeField.vue"

const CATEGORIES = [ "work", "personal", "ambitions", "health", "finance" ]
const PRIORITIES = [ "normal", "high", "urgent" ]

const store = useAiTaskConfirmStore()
const task = computed<SuggestedTask | null>(() => store.pending)

const saving = ref(false)
const error = ref<string | null>(null)
const form = reactive({
  title: "",
  category: "personal",
  priority: "normal",
  due_at: "",
  repeat_rule: "",
})

function endOfTodayLocal(): string {
  const d = new Date()
  d.setHours(23, 0, 0, 0)
  const pad = (n: number) => n.toString().padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

watch(task, (value) => {
  if (!value) return
  form.title = value.title || ""
  form.category = value.category || "personal"
  form.priority = value.priority || "normal"
  form.due_at = value.due_at ? value.due_at.slice(0, 16) : endOfTodayLocal()
  form.repeat_rule = value.repeat_rule || ""
  error.value = null
}, { immediate: true })

async function onSave() {
  saving.value = true
  error.value = null
  try {
    await apiClient.post("/tasks/accept", {
      task: {
        title: form.title,
        category: form.category,
        priority: form.priority,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        repeat_rule: form.repeat_rule || null,
      },
    })
    store.accepted()
  } catch (e: unknown) {
    interface ErrorResponse { response?: { data?: { errors?: string[] } } }
    const err = e as ErrorResponse
    error.value = err.response?.data?.errors?.join(", ") || "Couldn't save."
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.chip-row, .priority-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.375rem;
}
</style>
